import { useMemo, useState } from "react";
import { BackupBar } from "@/components/BackupBar";
import { useStored } from "../travel/tripStore";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, X, Download } from "lucide-react";
import { toast } from "sonner";
import { PDFDocument, rgb } from "pdf-lib";
import { TextKit } from "../business/pdfText";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { parseLocalDate } from "../travel/tripStore";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { AISLES, AUTO_AISLE, addGroceryItem, mergeDuplicates, classifyAisle } from "./groceryAisles";
import { copyText } from "@/lib/copyText";

// A short, genuinely common starting list per aisle -- shown as
// one-click suggestion chips so building a routine list doesn't mean
// typing the same handful of staples every single week.
const SUGGESTED_ITEMS: Record<string, string[]> = {
  Produce: ["Bananas", "Apples", "Lettuce", "Tomatoes", "Onions", "Carrots", "Potatoes", "Avocado"],
  "Dairy & Eggs": ["Milk", "Eggs", "Butter", "Cheese", "Yogurt", "Cream"],
  "Meat & Seafood": ["Chicken breast", "Ground beef", "Bacon", "Salmon", "Shrimp"],
  Bakery: ["Bread", "Bagels", "Tortillas", "Rolls"],
  Frozen: ["Frozen vegetables", "Ice cream", "Frozen pizza", "Frozen fruit"],
  Pantry: ["Rice", "Pasta", "Olive oil", "Canned beans", "Cereal", "Coffee", "Flour", "Sugar"],
  Household: ["Paper towels", "Toilet paper", "Dish soap", "Laundry detergent", "Trash bags"],
  Other: [],
};

interface Item {
  id: number;
  name: string;
  aisle: string;
  qty: string;
  checked: boolean;
}

const TOOL_INFO_CONTENT = {
  what: `Builds a grocery list organized by aisle — produce, dairy, meat, and so on — so shopping follows a logical path through the store instead of a random order.`,
  how: `Add items with a quantity and aisle category, check them off as you shop, and download the organized list as a PDF to share, or copy it as text.`,
  tip: `Add items to the list throughout the week as you notice you're running low on something, rather than trying to remember everything in one sitting right before you shop.`,
};

export default function GroceryListPlanner() {
  const [items, setItems] = useStored<Item[]>("fdp-GroceryListPlanner-items-v1", [
    { id: 1, name: "Milk", aisle: "Dairy & Eggs", qty: "1", checked: false },
    { id: 2, name: "Bananas", aisle: "Produce", qty: "6", checked: false },
    {
      id: 3,
      name: "Chicken breast",
      aisle: "Meat & Seafood",
      qty: "2 lb",
      checked: false,
    },
  ]);
  const [nextId, setNextId] = useStored("fdp-GroceryListPlanner-nextId-v1", 4);
  const [newName, setNewName] = useState("");
  const [newAisle, setNewAisle] = useState(AUTO_AISLE);
  const [newQty, setNewQty] = useState("1");
  const [shoppingDate, setShoppingDate] = useStored("fdp-GroceryListPlanner-shoppingDate-v1", "");

  const addItem = () => {
    if (!newName.trim()) return;
    setItems(addGroceryItem(items, { id: nextId, name: newName, aisle: newAisle, qty: newQty }));
    setNextId(nextId + 1);
    setNewName("");
  };

  // Adds a suggested item directly, skipping the name-typing step
  // entirely -- if it's already on the list for this aisle, does
  // nothing rather than adding a silent duplicate.
  const addSuggested = (name: string) => {
    const aisle =
      newAisle !== AUTO_AISLE
        ? newAisle
        : (Object.keys(SUGGESTED_ITEMS).find(a => SUGGESTED_ITEMS[a].includes(name)) ?? classifyAisle(name));
    const next = addGroceryItem(items, { id: nextId, name, aisle, qty: "1" });
    if (next.length === items.length) {
      toast.error(`${name} is already on your list.`);
      return;
    }
    setItems(next);
    setNextId(id => id + 1);
  };

  const suggestions =
    newAisle === AUTO_AISLE ? Object.values(SUGGESTED_ITEMS).flat() : (SUGGESTED_ITEMS[newAisle] ?? []);

  const toggle = (id: number) =>
    setItems(items.map(i => (i.id === id ? { ...i, checked: !i.checked } : i)));
  const remove = (id: number) => setItems(items.filter(i => i.id !== id));

  const grouped = useMemo(() => {
    const map: Record<string, Item[]> = {};
    for (const aisle of AISLES) map[aisle] = [];
    for (const item of mergeDuplicates(items)) {
      if (!map[item.aisle]) map[item.aisle] = [];
      map[item.aisle].push(item);
    }
    return Object.entries(map).filter(([, list]) => list.length > 0);
  }, [items]);

  const { logUsageNow } = useLogUsageOnAction();

  const copyList = async () => {
    const allowed = await logUsageNow("copy");
    if (!allowed) return;
    let text = shoppingDate
      ? `Grocery List — ${new Date(`${shoppingDate}T00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}\n\n`
      : "";
    for (const [aisle, list] of grouped) {
      text += `${aisle}:\n`;
      list.forEach(
        i => (text += `${i.checked ? "[x]" : "[ ]"} ${i.name} (${i.qty})\n`)
      );
      text += "\n";
    }
    void copyText(text.trim(), "Grocery list copied");
  };

  const downloadPdf = async () => {
    const allowed = await logUsageNow();
    if (!allowed) return;
    const pdfDoc = await PDFDocument.create();
    const dateText = shoppingDate ? (parseLocalDate(shoppingDate) ?? new Date()).toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" }) : "";
    const allText = ["Grocery List", dateText, ...Array.from(grouped).flatMap(([aisle, list]) => [aisle, ...list.map(i => `${i.name}  (${i.qty})`)])].join("\n");
    const kit = await TextKit.create(pdfDoc, allText);
    const pageWidth = 612, pageHeight = 792, margin = 54;
    let page = pdfDoc.addPage([pageWidth, pageHeight]);
    let y = pageHeight - margin;

    const ensureSpace = (needed: number) => {
      if (y - needed < margin) {
        page = pdfDoc.addPage([pageWidth, pageHeight]);
        y = pageHeight - margin;
      }
    };

    kit.draw(page, "Grocery List", { x: margin, y, size: 20, bold: true });
    y -= 26;
    if (shoppingDate) {
      kit.draw(page, dateText, { x: margin, y, size: 11, color: rgb(0.4, 0.4, 0.4) });
      y -= 20;
    }
    y -= 8;

    for (const [aisle, list] of grouped) {
      ensureSpace(30);
      kit.draw(page, aisle, { x: margin, y, size: 13, bold: true });
      y -= 6;
      page.drawLine({ start: { x: margin, y }, end: { x: pageWidth - margin, y }, thickness: 0.5, color: rgb(0.6, 0.6, 0.6) });
      y -= 18;

      for (const item of list) {
        ensureSpace(20);
        // A hollow square checkbox, hand-drawn since pdf-lib has no
        // built-in form checkbox glyph -- checked items get an X inside.
        page.drawRectangle({ x: margin, y: y - 9, width: 11, height: 11, borderColor: rgb(0.3, 0.3, 0.3), borderWidth: 1 });
        if (item.checked) {
          page.drawLine({ start: { x: margin + 1, y: y - 8 }, end: { x: margin + 10, y: y + 1 }, thickness: 1, color: rgb(0.2, 0.2, 0.2) });
          page.drawLine({ start: { x: margin + 1, y: y + 1 }, end: { x: margin + 10, y: y - 8 }, thickness: 1, color: rgb(0.2, 0.2, 0.2) });
        }
        const label = `${item.name}  (${item.qty})`;
        kit.draw(page, label, { x: margin + 20, y, size: 11 });
        y -= 20;
      }
      y -= 10;
    }

    await kit.finish();
    const pdfBytes = await pdfDoc.save();
    downloadBlob(new Blob([new Uint8Array(pdfBytes)], { type: "application/pdf" }), "grocery-list.pdf");
    toast.success("Grocery list downloaded!");
  };

  return (
    <ToolContainer>
      <div className="flex items-end gap-4 mb-4">
        <div className="max-w-xs">
          <label className="text-xs text-muted-foreground mb-1 block">Shopping Date (optional)</label>
          <Input type="date" value={shoppingDate} onChange={e => setShoppingDate(e.target.value)} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_150px_80px_40px] gap-2 mb-2">
        <Input
          value={newName}
          onChange={e => setNewName(e.target.value)}
          placeholder="Add an item..."
          onKeyDown={e => e.key === "Enter" && addItem()}
        />
        <Select value={newAisle} onValueChange={setNewAisle}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={AUTO_AISLE}>Auto (detect aisle)</SelectItem>
            {AISLES.map(a => (
              <SelectItem key={a} value={a}>
                {a}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={newQty}
          onChange={e => setNewQty(e.target.value)}
          placeholder="Qty"
        />
        <Button onClick={addItem}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-6">
          <span className="text-xs text-muted-foreground self-center mr-1">
            {newAisle === AUTO_AISLE ? "Common items:" : `Common ${newAisle.toLowerCase()} items:`}
          </span>
          {suggestions.map(name => (
            <button
              key={name}
              onClick={() => addSuggested(name)}
              className="tool-chip text-xs"
            >
              + {name}
            </button>
          ))}
        </div>
      )}

      <div className="space-y-6">
        {grouped.map(([aisle, list]) => (
          <div key={aisle}>
            <p className="text-sm font-semibold text-muted-foreground mb-2">
              {aisle}
            </p>
            <div className="space-y-1">
              {list.map(item => (
                <div
                  key={item.id}
                  className="flex items-center gap-2 py-1 px-2 rounded hover:bg-muted/50"
                >
                  <input
                    type="checkbox"
                    checked={item.checked}
                    onChange={() => toggle(item.id)}
                  />
                  <span
                    className={
                      item.checked ? "line-through text-muted-foreground" : ""
                    }
                  >
                    {item.name}{" "}
                    <span className="text-xs text-muted-foreground">
                      ({item.qty})
                    </span>
                  </span>
                  <button
                    onClick={() => remove(item.id)}
                    className="ml-auto text-muted-foreground hover:text-red-500"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-3 mt-6">
        <Button variant="outline" onClick={copyList}>
          Copy List
        </Button>
        <Button onClick={downloadPdf} className="gap-2">
          <Download className="h-4 w-4" /> Download PDF
        </Button>
      </div>
          <BackupBar tool="grocery-list-planner" prefixes={["fdp-GroceryListPlanner-"]} session />
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
